import React, { useEffect, useState } from 'react';
import { useLocation, Outlet, Link, useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import useModalContext from 'hooks/useModalContext';
import { T, O, M, A } from 'TOMA';
import { InputMedias, InputText, InputTextarea } from 'form';
import { eRoles } from 'interfaces/Roles';
import s from './Event.module.scss';
import useRepo from '../../hooks/useRepo';
import routes from '../../router/routes';
import {
  LocalizationProvider,
  DatePicker,
  DesktopDatePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { Switch, FormControlLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { iEventRest } from '../../services/Event/Event.type';
import { use } from 'i18next';
import FormControl from '@mui/material/FormControl';
import EventQuestions from '../../components/FormInformation/EventQuestions';
import EventModules from '../Ticketing/EventModules';

export interface iForm {
  city: string;
  date: string;
  country: string;
  url: string;
  name: string;
  number_of_samples: string;
  showroom: boolean;
  show_samples_information: boolean;
  address: string;
  limit_date: string;
  deadline_date: string;
  date_of_the_end: string;
  subtitle: string;
  website_title?: string | null;
  animation_title?: string | null;
  animation_description?: string | null;
  footer_text: string;
  contact_email_1: string;
  contact_email_2: string;
  logo: number;
  number_of_products?: string;
  thematic_selection_count?: number;
  thematic_enabled?: boolean;
  catalog_products_limit?: number;
  color?: string | null;
  thematic_title?: string | null;
  questionnaire_title?: string | null;
  thematic_description?: string | null;
  thematiques?: Array<{
    name: string;
    limit: number;
    color: string;
    second_color?: string;
  }>;
  visibility?: boolean;
  showroom_logo?: number;
  header_logo_left?: number | null;
  header_logo_left_two?: number | null;
  header_logo_right?: number | null;
  facturation_code?: string;
  slug?: string;
}

const stripYearFromSlug = (slug?: string | null): string =>
  (slug ?? '').replace(/-(19|20)\d{2}$/, '');

// https://{storefront}/events/{slug}-{year}
const buildEventUrl = (
  slug?: string | null,
  year?: string | number | null
): string => {
  const base = (
    process.env.REACT_APP_STOREFRONT_WEB_URL ?? window.location.origin
  ).replace(/\/+$/, '');
  const cleanSlug = stripYearFromSlug(slug);
  if (!cleanSlug) return '';
  return `${base}/events/${cleanSlug}${year ? `-${String(year)}` : ''}`;
};

const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function Event(): JSX.Element {
  const { setModalContent } = useModalContext();
  const {
    eventRepository,
    userRepository,
    mediaRepository,
    categoryRepository,
  } = useApiContext();
  const { pathname } = useLocation();
  const user = userRepository.getUser();
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const [file, setFile] = useState('');
  const { t } = useTranslation();
  const [eventExtraFields, setEventExtraFields] = useState<any>([]);
  const [eventData, setEventData] = useState<iEventRest | null>(null);
  const eventYears: number[] = Array.from(
    { length: 2045 - 2025 + 1 },
    (_, i) => 2025 + i
  );

  const [value, setValuedate] = useState<any>(null);
  const isAdmin =
    user?.role === eRoles.ADMIN || user?.role === eRoles.MODERATOR;
  const { eventId } = useParams();
  const eventCreator = eventData?.creator as { email?: string } | undefined;
  const isEventOwner =
    user?.role !== eRoles.MODERATOR ||
    !eventId ||
    (!!user?.email && user.email === eventCreator?.email);
  const schema = yup.object({
    date: yup.string().max(255).required('This field is required').nullable(),
    country: yup
      .string()
      .max(255)
      .required('This field is required')
      .nullable(),
    name: yup.string().max(255).required('This field is required').nullable(),
    website_title: yup.string().max(255).nullable().notRequired(),
    subtitle: yup
      .string()
      .max(255)
      .required('This field is required')
      .nullable(),
    category_id: yup.string().required('This field is required').nullable(),
    year: yup.string().required('This field is required').nullable(),
    contact_email_1: yup
      .string()
      .email('Invalid email format')
      .max(255)
      .required('This field is required')
      .nullable(),
    contact_email_2: yup
      .string()
      .email('Invalid email format')
      .max(255)
      .required('This field is required')
      .nullable(),
    logo: yup.string().required('This field is required').nullable(),
    city: yup.string().max(255).required('This field is required').nullable(),
  });
  const form = useForm<iForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: { thematiques: [] },
  });
  const { response, loading } = useRepo(
    async () =>
      eventId !== undefined
        ? await eventRepository.getEventById(eventId).then((e) => e)
        : '',
    [pathname]
  );

  const { response: categories } = useRepo(async () => {
    return await categoryRepository.parentCategory('universe');
  }, [pathname]);

  useEffect(() => {
    if (eventId && !loading && response) {
      const fields = response?.event_extra_fields;

      setEventExtraFields(fields);
      setEventData(response);
      // Prepare form data with event extra fields values
      const formData: any = { ...response };
      // "Event URL du e-catalogue": default to https://{storefront}/events/{slug}-{year}
      if (!String(formData.url ?? '').trim()) {
        formData.url = buildEventUrl(response.slug, response.year);
      }
      if (fields && fields?.length > 0) {
        formData.thematiques = fields.map((f: any) => ({
          name: f.field_value,
          limit: f.limit,
          color: f.color,
          second_color: f.second_color,
        }));
      }
      // Update form values when response is available
      reset(formData);
    }
  }, [loading, response, form.reset]);
  const { handleSubmit, watch, control, reset, setValue, getValues } = form;
  // On creation, prefill the URL from the event name until the admin types their own
  const lastDerivedUrl = React.useRef('');
  const watchedName = watch('name' as any) as string | undefined;
  const watchedYear = watch('year' as any) as string | number | undefined;
  useEffect(() => {
    if (eventId) return;
    const current = getValues('url') ?? '';
    if (current === '' || current === lastDerivedUrl.current) {
      const derived = buildEventUrl(slugify(watchedName ?? ''), watchedYear);
      lastDerivedUrl.current = derived;
      setValue('url', derived);
    }
  }, [watchedName, watchedYear, eventId]);
  const [showSamples, setShowSamples] = useState(false);
  const showSamplesValue = watch('show_samples_information');
  useEffect(() => {
    setShowSamples(!showSamplesValue);
  }, [showSamplesValue]);
  const questionsSaveRef = React.useRef<(() => Promise<void>) | null>(null);
  function onSubmit(formData: iForm): void {
    // a removed header logo is `undefined`; send null so the backend clears it
    const data: iForm = {
      ...formData,
      header_logo_left: formData.header_logo_left ?? null,
      header_logo_left_two: formData.header_logo_left_two ?? null,
      header_logo_right: formData.header_logo_right ?? null,
    };
    setLoadingForm(true);
    if (eventId) {
      // questions are stored by their own API; save them together with the event
      Promise.resolve(questionsSaveRef.current?.())
        .then(
          async () => await eventRepository.updateFormInformation(data, eventId)
        )
        .then(() => {
          setSuccessForm(true);
          setErrorForm('');
        })
        .catch((err: Error) => {
          setErrorForm(err.message);
        })
        .finally(() => {
          setLoadingForm(false);
          if (pathname.includes('new-event')) {
            window.location.href = routes.home2.path + 'events';
          } else {
            setTimeout(() => {
              setSuccessForm(false);
            }, 3000);
          }
        });
    } else {
      eventRepository
        .createEvent(data)
        .then(() => {
          setSuccessForm(true);
          setErrorForm('');
        })
        .catch((err: Error) => {
          setErrorForm(err.message);
        })
        .finally(() => {
          setLoadingForm(false);
          if (pathname.includes('new-event')) {
            window.location.href = routes.home2.path + 'events';
          } else {
            setTimeout(() => {
              setSuccessForm(false);
            }, 3000);
          }
        });
    }
  }
  const createdFromRef = React.useRef(null);
  const createdFromRef1 = React.useRef(null);
  const createdFromRef2 = React.useRef(null);
  const createdFromRef3 = React.useRef(null);
  const [openCreatedFrom, setOpenCreatedFrom] = useState(false);
  const [openCreatedFrom1, setOpenCreatedFrom1] = useState(false);
  const [openCreatedFrom2, setOpenCreatedFrom2] = useState(false);
  const [openCreatedFrom3, setOpenCreatedFrom3] = useState(false);
  const mediaList: Array<
    'media_1' | 'media_2' | 'media_3' | 'media_4' | 'media_5'
  > = ['media_1', 'media_2', 'media_3', 'media_4', 'media_5'];
  return (
    <div>
      <div className={pathname !== '/' && !isAdmin ? s.blur : undefined}>
        <M.Loading loading={false} error={null} fullWiewport>
          <T.Block
            w={pathname.includes('new-event') ? '100' : '75'}
            mx={pathname.includes('new-event') ? '0' : 'auto'}
            bg="white"
            borderRadius="20"
          >
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <T.Block>
                <T.Block p="20">
                  <A.Title
                    as="h2"
                    textAlign="center"
                    fontSize="24"
                    mb="20"
                    fw="black"
                  >
                    {t('event:event_informations')}
                  </A.Title>
                  <A.Line mb="20" />
                  <T.Block
                    ml={!eventId ? '50' : '0'}
                    w={!eventId ? '75' : '100'}
                  >
                    <InputText
                      label={t('event:name_of_the_event')}
                      name="name"
                      type="text"
                      control={control}
                      required
                    />
                    <InputText
                      label={t('event:subtitle_of_the_event')}
                      name="subtitle"
                      type="text"
                      control={control}
                      required
                    />
                    <Controller
                      name={'year' as any}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div>
                          <label
                            style={{
                              fontSize: '16px',
                              fontWeight: 500,
                              color: error ? '#ea6863' : '#414141ff',
                            }}
                          >
                            {t('event:year_of_the_event') || 'Year'}*
                          </label>
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const year = e.target.value as number;
                              field.onChange(year);
                            }}
                            displayEmpty
                            fullWidth
                            error={!!error}
                            sx={{
                              marginTop: '10px',
                              marginBottom: '10px',
                              fontWeight: 400,
                              fontSize: '18px',
                            }}
                          >
                            <MenuItem value="">
                              {t('event:select_year') || 'Select year'}
                            </MenuItem>
                            {eventYears.map((y) => (
                              <MenuItem key={y} value={y}>
                                {y}
                              </MenuItem>
                            ))}
                          </Select>
                          {error && (
                            <p
                              style={{
                                color: '#ea6863',
                                fontSize: '14px',
                                marginBottom: '15px',
                              }}
                            >
                              {error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <Controller
                      name={'category_id' as any}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div>
                          <label
                            style={{
                              fontSize: '15px',
                              fontWeight: 500,
                              color: error ? '#ea6863' : '#414141ff',
                            }}
                          >
                            {t('event:category') || 'Category'}*
                          </label>
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const categoryId = e.target.value as number;
                              field.onChange(categoryId);
                            }}
                            displayEmpty
                            fullWidth
                            error={!!error}
                            sx={{
                              marginTop: '10px',
                              marginBottom: '10px',
                              fontWeight: 400,
                              fontSize: '18px',
                            }}
                          >
                            <MenuItem value="">
                              {t('event:select_category') || 'Select category'}
                            </MenuItem>
                            {categories?.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.topicCode}
                              </MenuItem>
                            ))}
                          </Select>
                          {error && (
                            <p
                              style={{
                                color: '#ea6863',
                                fontSize: '14px',
                                marginBottom: '15px',
                              }}
                            >
                              {error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <Controller
                      name="date"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <label
                            style={{
                              color: error ? '#ea6863' : '#414141',
                              fontSize: '16px',
                              fontWeight: 500,
                              marginBottom: 'auto',
                            }}
                          >
                            {t('event:start_event')}*
                          </label>
                          <DesktopDatePicker
                            format="dd/MM/yyyy"
                            views={['year', 'month', 'day']}
                            sx={{
                              width: '100%',
                              marginTop: '10px',
                              marginBottom: '10px',
                            }}
                            value={
                              field.value
                                ? parse(field.value, 'yyyy-MM-dd', new Date())
                                : null
                            }
                            open={openCreatedFrom}
                            onClose={() => {
                              setOpenCreatedFrom(false);
                            }}
                            onOpen={() => {
                              setOpenCreatedFrom(true);
                            }}
                            onChange={(newValue) => {
                              field.onChange(
                                newValue ? format(newValue, 'yyyy-MM-dd') : ''
                              );
                            }}
                            slotProps={{
                              textField: {
                                inputRef: createdFromRef,
                                onClick: () =>
                                  setOpenCreatedFrom((prev) => !prev),
                                error: !!error,
                                helperText: error?.message,
                                FormHelperTextProps: {
                                  sx: {
                                    marginLeft: 0,
                                    color: '#ea6863',
                                    fontSize: '14px',
                                  },
                                },

                                InputProps: {
                                  sx: {
                                    '& .MuiInputBase-input': {
                                      fontSize: '18px',
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    <Controller
                      name="date_of_the_end"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <label
                            className="InputText_InputText__label__FJvG7"
                            style={{ marginBottom: 'auto' }}
                          >
                            {t('event:end_event')}
                          </label>
                          <DesktopDatePicker
                            format="dd/MM/yyyy"
                            views={['year', 'month', 'day']}
                            sx={{
                              width: '100%',
                              marginTop: '10px',
                              marginBottom: '10px',
                            }}
                            value={
                              field.value
                                ? parse(field.value, 'yyyy-MM-dd', new Date())
                                : null
                            }
                            open={openCreatedFrom3}
                            onClose={() => {
                              setOpenCreatedFrom3(false);
                            }}
                            onOpen={() => {
                              setOpenCreatedFrom3(true);
                            }}
                            onChange={(newValue) => {
                              field.onChange(
                                newValue ? format(newValue, 'yyyy-MM-dd') : ''
                              );
                            }}
                            slotProps={{
                              textField: {
                                inputRef: createdFromRef3,
                                onClick: () => {
                                  setOpenCreatedFrom3((prev) => !prev);
                                },
                                InputProps: {
                                  sx: {
                                    '& .MuiInputBase-input': {
                                      fontWeight: 400,
                                      fontSize: '18px',
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    <InputText
                      label={t('common:email_1')}
                      name="contact_email_1"
                      type="text"
                      control={control}
                      required
                    />
                    <InputText
                      label={t('common:email_2')}
                      name="contact_email_2"
                      type="text"
                      control={control}
                      required
                    />
                    <T.Block>
                      <InputMedias
                        label={t('event:logo')}
                        // information={t('event:logo')}
                        name="logo"
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          if (result) {
                            setFile(result.id);
                            return result;
                          }
                          return null;
                        }}
                        onRemove={async (mediaId) => {
                          return await mediaRepository
                            .remove('logo', mediaId)
                            .then((e) => {
                              setFile('');
                            });
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                        required
                      />
                    </T.Block>
                    <Controller
                      name="color"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label
                            style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontSize: '16px',
                              color: 'var(--tertiary)',
                            }}
                          >
                            {t('event:event_color')}
                          </label>
                          <input
                            type="color"
                            {...field}
                            value={field.value ?? '#000000'}
                            style={{ marginBottom: '10px' }}
                          />
                        </div>
                      )}
                    />
                    <InputText
                      label={t('event:city_of_event')}
                      name="city"
                      type="text"
                      control={control}
                      required
                    />

                    <InputText
                      label={t('event:country_of_event')}
                      name="country"
                      type="text"
                      control={control}
                      required
                    />
                    <Controller
                      name="deadline_date"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <label>{t('event:registration_closing_date')}</label>
                          <DesktopDatePicker
                            format="dd/MM/yyyy"
                            views={['year', 'month', 'day']}
                            sx={{
                              width: '100%',
                              marginTop: '10px',
                              marginBottom: '10px',
                            }}
                            value={
                              field.value
                                ? parse(field.value, 'yyyy-MM-dd', new Date())
                                : null
                            }
                            open={openCreatedFrom1}
                            onClose={() => {
                              setOpenCreatedFrom1(false);
                            }}
                            onOpen={() => {
                              setOpenCreatedFrom1(true);
                            }}
                            onChange={(newValue) => {
                              field.onChange(
                                newValue ? format(newValue, 'yyyy-MM-dd') : ''
                              );
                            }}
                            slotProps={{
                              textField: {
                                inputRef: createdFromRef1,
                                onClick: () => {
                                  setOpenCreatedFrom1((prev) => !prev);
                                },
                                InputProps: {
                                  sx: {
                                    '& .MuiInputBase-input': {
                                      fontWeight: 400,
                                      fontSize: '18px',
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    <InputText
                      label={t('event:e_catalogue_url')}
                      name="url"
                      type="text"
                      control={control}
                      disabled={
                        (user?.role === eRoles.MODERATOR ||
                          user?.role === eRoles.ADMIN) &&
                        eventData?.visibility === 0
                      }
                    />
                    <InputText
                      label={t('event:facturation_code')}
                      name="facturation_code"
                      type="text"
                      control={control}
                      disabled={
                        (user?.role === eRoles.MODERATOR ||
                          user?.role === eRoles.ADMIN) &&
                        eventData?.visibility === 0
                      }
                    />
                    <div style={{ marginBottom: 16 }}>
                      <InputText
                        label="Slug (URL publique)"
                        name="slug"
                        type="text"
                        control={control}
                        disabled
                      />
                      {watch('slug' as any) && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 11,
                            color: '#666',
                            wordBreak: 'break-all',
                          }}
                        >
                          🔗 URL&nbsp;:&nbsp;
                          <span style={{ color: '#1f4e9c' }}>
                            {`${window.location.origin}/${
                              window.location.pathname
                                .split('/')
                                .filter(Boolean)[0]
                            }/published/${watch('slug' as any)}`}
                          </span>
                        </div>
                      )}
                    </div>
                    <Controller
                      name="showroom"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#28AB6E',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: 'rgb(0, 163, 3)',
                                  },
                                },
                              }}
                              {...field}
                              checked={!!field.value} // Convert to boolean
                            />
                          }
                          label={t('event:animation_on_event')}
                          labelPlacement="end" // This places the label on the right and switch on the left
                        />
                      )}
                    />

                    <Controller
                      name="show_samples_information"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#28AB6E',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: 'rgb(0, 163, 3)',
                                  },
                                },
                              }}
                              {...field}
                              checked={!!field.value} // Convert to boolean
                              onChange={(e) => {
                                field.onChange(e.target.checked); // Update the field value
                                setShowSamples(e.target.checked); // Update the local state
                              }}
                            />
                          }
                          label={t('event:sample_informations')}
                          labelPlacement="end" // Places the label on the right and switch on the left
                        />
                      )}
                    />
                    <A.Title
                      as="h2"
                      fontSize="24"
                      textAlign="center"
                      mt="20"
                      mb="20"
                      fw="black"
                    >
                      {t('event:website_informations')}
                    </A.Title>
                    <InputText
                      label={t('event:website_title')}
                      name="website_title"
                      type="text"
                      control={control}
                    />
                    <T.Block>
                      <InputMedias
                        label={t('event:header_logo_left')}
                        name="header_logo_left"
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          return result || null;
                        }}
                        onRemove={async (mediaId) => {
                          await mediaRepository.remove('logo', mediaId);
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                      />
                    </T.Block>
                    <T.Block>
                      <InputMedias
                        label={t('event:header_logo_left_two')}
                        name="header_logo_left_two"
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          return result || null;
                        }}
                        onRemove={async (mediaId) => {
                          await mediaRepository.remove('logo', mediaId);
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                      />
                    </T.Block>
                    <T.Block>
                      <InputMedias
                        label={t('event:header_logo_right')}
                        name="header_logo_right"
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          return result || null;
                        }}
                        onRemove={async (mediaId) => {
                          await mediaRepository.remove('logo', mediaId);
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                      />
                    </T.Block>
                    {(user?.role === 'ADMIN' || user?.role === 'MODERATOR') &&
                      watch('showroom') && (
                        <>
                          <A.Title
                            as="h2"
                            fontSize="24"
                            textAlign="center"
                            mt="20"
                            mb="20"
                            fw="black"
                          >
                            {t('event:animation_informations')}
                          </A.Title>
                          <T.Block>
                            <InputMedias
                              label={t('event:logo')}
                              name="showroom_logo"
                              control={control}
                              onInit={async (mediaId) => {
                                return await mediaRepository
                                  .get(mediaId)
                                  .then(({ id, w640, filename }) => ({
                                    id,
                                    src: w640,
                                    filename,
                                  }));
                              }}
                              onChange={async (file) => {
                                const formdata = new FormData();
                                formdata.append('media', file); // ✔ keep same as working version

                                const result = await mediaRepository.upload(
                                  formdata
                                );
                                if (result) {
                                  setFile(result.id);
                                  return result;
                                }
                                return null;
                              }}
                              onRemove={async (mediaId) => {
                                return await mediaRepository
                                  .remove('logo', mediaId) // ✔ use correct backend name
                                  .then(() => setFile(''));
                              }}
                              onZoom={(src) => {
                                setModalContent(
                                  <img src={src} style={{ maxWidth: '100%' }} />
                                );
                              }}
                            />
                          </T.Block>
                          <InputText
                            label={t('event:animation_title')}
                            name="animation_title"
                            type="text"
                            control={control}
                          />
                          <InputTextarea
                            label={t('event:animation_description')}
                            name="animation_description"
                            control={control}
                            rows={5}
                          />
                          <Controller
                            name="catalog_products_limit"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '16px',
                                    color: 'var(--tertiary)',
                                  }}
                                >
                                  {t('event:catalog_products_limit')}
                                </label>
                                <Select
                                  {...field}
                                  value={field.value ?? 10}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  fullWidth
                                  sx={{
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  {Array.from({ length: 50 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                      {i + 1}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                            )}
                          />
                          <Controller
                            name="number_of_products"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '16px',
                                    color: 'var(--tertiary)',
                                  }}
                                >
                                  {t('event:number_of_products')}
                                </label>
                                <Select
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  displayEmpty
                                  fullWidth
                                  sx={{
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <MenuItem value="">
                                    {t('event:select_number_of_products')}
                                  </MenuItem>
                                  {Array.from({ length: 11 }, (_, i) => (
                                    <MenuItem key={i} value={i}>
                                      {i}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                            )}
                          />
                          <Controller
                            name="thematic_enabled"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Switch
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#28AB6E',
                                        '& + .MuiSwitch-track': {
                                          backgroundColor: 'rgb(0, 163, 3)',
                                        },
                                      },
                                    }}
                                    checked={
                                      field.value === undefined ||
                                      field.value === null ||
                                      !!Number(field.value)
                                    }
                                    onChange={(e) =>
                                      field.onChange(e.target.checked)
                                    }
                                  />
                                }
                                label={t('event:thematic_enabled')}
                                labelPlacement="end"
                              />
                            )}
                          />
                          <InputText
                            label={t('event:thematic_title')}
                            name="thematic_title"
                            type="text"
                            control={control}
                          />
                          <InputTextarea
                            label={t('event:thematic_description')}
                            name="thematic_description"
                            control={control}
                            rows={4}
                          />
                          <Controller
                            name="thematic_selection_count"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '16px',
                                    color: 'var(--tertiary)',
                                  }}
                                >
                                  {t('event:thematic_selection_count')}
                                </label>
                                <Select
                                  {...field}
                                  value={field.value ?? 3}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  fullWidth
                                  sx={{
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  {Array.from({ length: 12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                      {i + 1}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                            )}
                          />
                          {Array.from({ length: 12 }, (_, i) => (
                            <T.Flex mt="20" mb="10" gap={5} key={i}>
                              <FormControl
                                sx={{
                                  width: '100%',
                                }}
                              >
                                {' '}
                                <InputText
                                  key={`${i}-name`}
                                  label={t(`event:thematic`) + ` ${i + 1}`}
                                  name={`thematiques.${i}.name` as const} // Access array index
                                  type="text"
                                  control={control}
                                />
                              </FormControl>
                              <FormControl
                                sx={{
                                  width: '20%',
                                }}
                              >
                                <InputText
                                  key={`${i}-limit`}
                                  type="number"
                                  label={t(`event:limit`)}
                                  name={`thematiques.${i}.limit` as const}
                                  control={control}
                                />
                              </FormControl>
                              <FormControl
                                sx={{
                                  width: '20%',
                                }}
                              >
                                <InputText
                                  key={`${i}-color`}
                                  type="text"
                                  label={t(`event:color`)}
                                  name={`thematiques.${i}.color` as const}
                                  control={control}
                                />
                              </FormControl>
                              <FormControl
                                sx={{
                                  width: '20%',
                                }}
                              >
                                <InputText
                                  key={`${i}-second-color`}
                                  type="text"
                                  label={t(`event:second_color`)}
                                  name={
                                    `thematiques.${i}.second_color` as const
                                  }
                                  control={control}
                                />
                              </FormControl>
                            </T.Flex>
                          ))}
                        </>
                      )}
                    {isAdmin && eventId && (
                      // question forms save on their own; keep their submits from reaching the event form
                      <div onSubmit={(e) => e.stopPropagation()}>
                        <EventQuestions
                          headerContent={
                            <InputText
                              label={t('event:questionnaire_title')}
                              name="questionnaire_title"
                              type="text"
                              control={control}
                            />
                          }
                          eventId={eventId}
                          canEdit={isEventOwner}
                          saveRef={questionsSaveRef}
                        />
                      </div>
                    )}
                  </T.Block>
                  {!showSamples ? (
                    <>
                      <A.Title
                        as="h2"
                        fontSize="24"
                        textAlign="center"
                        mt="20"
                        mb="20"
                        fw="black"
                      >
                        {t('event:sample_informations')}
                      </A.Title>
                      <A.Line mb="20" />
                      <T.Block
                        ml={!eventId ? '50' : '0'}
                        w={!eventId ? '75' : '100'}
                      >
                        <InputText
                          label={t('event:numbers_of_sample_send')}
                          name="number_of_samples"
                          type="text"
                          control={control}
                          required
                          disabled={!isEventOwner}
                        />
                        <InputText
                          label={t('event:address_to_send_semple')}
                          name="address"
                          type="text"
                          control={control}
                          required
                          disabled={!isEventOwner}
                        />
                        <Controller
                          name="limit_date"
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <label
                                className="InputText_InputText__label__FJvG7"
                                style={{ marginBottom: 'auto' }}
                              >
                                {t('event:limit_date_to_receive_sample')}
                              </label>
                              <DesktopDatePicker
                                format="yyyy-MM-dd"
                                views={['year', 'month', 'day']}
                                disabled={!isEventOwner}
                                sx={{
                                  width: '100%',
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                }}
                                value={
                                  field.value
                                    ? parse(
                                        field.value,
                                        'yyyy-MM-dd',
                                        new Date()
                                      )
                                    : null
                                }
                                open={openCreatedFrom2}
                                onClose={() => {
                                  setOpenCreatedFrom2(false);
                                }}
                                onChange={(newValue) => {
                                  field.onChange(
                                    newValue
                                      ? format(newValue, 'yyyy-MM-dd')
                                      : ''
                                  );
                                }}
                                slotProps={{
                                  textField: {
                                    inputRef: createdFromRef2,
                                    onClick: () => {
                                      setOpenCreatedFrom2((prev) => !prev);
                                    },
                                    InputProps: {
                                      sx: {
                                        '& .MuiInputBase-input': {
                                          fontWeight: 400,
                                          fontSize: '18px',
                                        },
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </T.Block>
                    </>
                  ) : (
                    ''
                  )}
                  <A.Alert alert="success" side="🎉" mb="15">
                    {successForm && 'Created a new event successfully!'}
                  </A.Alert>
                  <A.Alert alert="error" side="⚠️" mb="15">
                    {errorForm && <A.InnerHTML html={errorForm} />}
                  </A.Alert>
                </T.Block>
                {isEventOwner && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <A.Button
                      type="button"
                      bg="success"
                      loading={loadingForm}
                      onClick={async () => {
                        handleSubmit(() => onSubmit(getValues()))();
                      }}
                      className={s.submit_button}
                      disabled={false}
                    >
                      {t('home:formInformation.save')}
                    </A.Button>
                  </div>
                )}
              </T.Block>
            </form>
          </T.Block>
          {eventId && user?.role === eRoles.ADMIN && (
            <T.Block w="75" mx="auto">
              <EventModules eventId={eventId} />
            </T.Block>
          )}
        </M.Loading>
      </div>
      {pathname !== '/' && !isAdmin && (
        <div className={s.Exhibitor__outlet}>
          <Link className={s.Exhibitor__outlet__close} to="/">
            <div>
              <A.Icon icon="x-lg" />
            </div>
          </Link>
          <div className={s.Exhibitor__outlet__content}>
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}

export default Event;
